import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Loader2, Tag, Plus, Edit3, Trash2, Save, Search, Upload,
  Image as ImageIcon, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import InfoTooltip from "@/components/admin/InfoTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminBrandsPage - Iter 9.33
 *
 * Brand database with logo upload (Cloudinary). Brands are linked to master
 * products via brand_id. Brand logo shows next to product name in pickers.
 */
export default function AdminBrandsPage() {
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({});

  const auth = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` }),
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        fetch(`${API}/admin/brands`, { headers: auth() }),
        fetch(`${API}/admin/category-master`, { headers: auth() }),
      ]);
      if (b.ok) setBrands((await b.json()).brands || []);
      if (c.ok) setCategories((await c.json()).masters || []);
    } finally {
      setLoading(false);
    }
  }, [auth]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = brands.filter((b) => {
    if (!search.trim()) return true;
    const t = search.trim().toLowerCase();
    return (
      (b.name || "").toLowerCase().includes(t) ||
      (b.category || "").toLowerCase().includes(t)
    );
  });

  const startEdit = (b) => {
    setEditingId(b.brand_id);
    setDraft({ ...b });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft({});
  };

  const saveEdit = async () => {
    const body = { ...draft };
    delete body.brand_id;
    const r = await fetch(`${API}/admin/brands/${editingId}`, {
      method: "PUT",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (r.ok) {
      toast.success("Saved");
      cancelEdit();
      load();
    } else {
      toast.error("Save failed");
    }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Soft-delete this brand?")) return;
    const r = await fetch(`${API}/admin/brands/${id}`, {
      method: "DELETE",
      headers: auth(),
    });
    if (r.ok) {
      toast.success("Removed");
      load();
    } else {
      toast.error("Delete failed");
    }
  };

  const createNew = async () => {
    if (!newDraft.name?.trim()) {
      toast.error("Name required");
      return;
    }
    const r = await fetch(`${API}/admin/brands`, {
      method: "POST",
      headers: { ...auth(), "Content-Type": "application/json" },
      body: JSON.stringify(newDraft),
    });
    if (r.ok) {
      toast.success("Brand created");
      setCreating(false);
      setNewDraft({});
      load();
    } else {
      toast.error("Create failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-brands-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">
            <Tag className="w-5 h-5" /> Brands
            <InfoTooltip
              label="Brands"
              description="Canonical brand catalog. Each brand has a logo (Cloudinary). Master products link by brand_id; the logo shows next to product names everywhere - pickers, host product cards, look-builder."
              source="db.brands"
              where_synced={["Master Products picker", "Host product cards", "Search results"]}
            />
          </h1>
          <p className="text-xs text-gray-500">
            Upload brand logos here so they show next to products everywhere.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-9"
              placeholder="Search brands..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="brand-search"
            />
          </div>
          <Button onClick={() => setCreating(true)} data-testid="brand-new-btn">
            <Plus className="w-4 h-4 mr-1" /> New brand
          </Button>
        </div>
      </div>

      {creating && (
        <Card className="mb-3 border-amber-300 bg-amber-50">
          <CardContent className="p-3">
            <BrandFieldsForm
              draft={newDraft}
              setDraft={setNewDraft}
              categories={categories}
              testidPrefix="new-brand"
            />
            <div className="flex gap-2 mt-2">
              <Button size="sm" onClick={createNew} data-testid="brand-new-save">
                <Save className="w-3.5 h-3.5 mr-1" /> Create
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setCreating(false); setNewDraft({}); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <div className="py-10 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-center text-sm text-gray-400 py-10">
          No brands yet. Click "New brand" above to add one.
        </p>
      ) : (
        <Card>
          <CardContent className="p-2">
            {filtered.map((b) =>
              editingId === b.brand_id ? (
                <div key={b.brand_id} className="border rounded p-3 bg-gray-50 mb-2">
                  <BrandFieldsForm
                    draft={draft}
                    setDraft={setDraft}
                    categories={categories}
                    testidPrefix={`edit-brand-${b.brand_id}`}
                  />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" onClick={saveEdit}>
                      <Save className="w-3.5 h-3.5 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={b.brand_id}
                  className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded border-b last:border-b-0"
                  data-testid={`brand-row-${b.brand_id}`}
                >
                  {b.logo_url ? (
                    <img
                      src={b.logo_url}
                      alt={b.name}
                      className="w-10 h-10 rounded object-contain bg-white border"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-100 border flex items-center justify-center">
                      <Tag className="w-4 h-4 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{b.name}</p>
                    <p className="text-[11px] text-gray-500">
                      {b.category || "no category"}
                      {b.website && (
                        <a
                          href={b.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-blue-600 inline-flex items-center"
                        >
                          <ExternalLink className="w-2.5 h-2.5 mr-0.5" />
                          {b.website.replace(/^https?:\/\//, "").slice(0, 30)}
                        </a>
                      )}
                    </p>
                  </div>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => startEdit(b)}
                    data-testid={`edit-brand-btn-${b.brand_id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => deleteRow(b.brand_id)}
                    data-testid={`delete-brand-btn-${b.brand_id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              )
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BrandFieldsForm({ draft, setDraft, categories, testidPrefix }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API}/upload/cloudinary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` },
        body: fd,
      });
      if (r.ok) {
        const d = await r.json();
        const url = d.url || d.secure_url;
        if (url) {
          setDraft({ ...draft, logo_url: url });
          toast.success("Logo uploaded");
        }
      } else toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      <div>
        <label className="text-xs text-gray-500 block mb-0.5">Brand name</label>
        <Input
          value={draft.name || ""}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          data-testid={`${testidPrefix}-name`}
        />
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-0.5">Primary category</label>
        <Select
          value={draft.category || ""}
          onValueChange={(v) => setDraft({ ...draft, category: v })}
        >
          <SelectTrigger data-testid={`${testidPrefix}-category`}>
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c.category_id} value={c.category_id}>
                {c.label || c.category_id}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="text-xs text-gray-500 block mb-0.5">Website</label>
        <Input
          value={draft.website || ""}
          onChange={(e) => setDraft({ ...draft, website: e.target.value })}
          placeholder="https://"
          data-testid={`${testidPrefix}-website`}
        />
      </div>
      <div className="sm:col-span-2">
        <label className="text-xs text-gray-500 block mb-0.5">Logo</label>
        <div className="flex items-center gap-3">
          <div className="relative w-14 h-14 group">
            {draft.logo_url ? (
              <>
                <img
                  src={draft.logo_url}
                  alt=""
                  className="w-14 h-14 rounded object-contain border bg-white"
                />
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, logo_url: "" })}
                  className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-black/80 hover:bg-black flex items-center justify-center text-white"
                  aria-label="Remove logo"
                  data-testid={`${testidPrefix}-logo-remove`}
                >
                  <span className="text-[10px] font-bold">×</span>
                </button>
              </>
            ) : (
              <div className="w-14 h-14 rounded bg-gray-100 border flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-gray-400" />
              </div>
            )}
          </div>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
            data-testid={`${testidPrefix}-logo-file`}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            data-testid={`${testidPrefix}-logo-upload-btn`}
          >
            {uploading ? (
              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
            ) : (
              <Upload className="w-3.5 h-3.5 mr-1" />
            )}
            {draft.logo_url ? "Replace logo" : "Upload logo"}
          </Button>
        </div>
      </div>
    </div>
  );
}
